import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEmpty } from "lodash";

import AsyncSelect from "react-select3/async";

import * as userActions from "../../actions/userActions";
import * as loaderActions from "../../actions/loaderActions";
import { customToast as toast } from "../../utils";

const UserSelect = ({
  value: initialValue,
  isMulti = false,
  onChange,
  name,
  users,
  actions,
  placeholder = "User",
  selectedDepartment,
  loading,
  disabled,
  projects,
  defaultValues,
  label
}) => {
  const [updatedValue, setUpdatedValue] = useState(isMulti ? [] : {});
  const [defaultOptions, setDefaultOptions] = useState([]);

  const [lastSelectedDepartment, setLastSelectedDepartment] = useState(null);

  const [lastProjects, setLastProjects] = useState(null);

  useEffect(() => {
    if (isEmpty(users)) {
      actions.loadUsers();
    }
  }, [actions, users]);

  useEffect(() => {
    if (initialValue) {
      setUpdatedValue(initialValue?.id || initialValue?.value || initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (
      defaultValues &&
      disabled &&
      !initialValue &&
      isMulti &&
      defaultOptions &&
      Array.isArray(defaultOptions) &&
      defaultOptions.length > 0
    ) {
      const values = defaultOptions.filter((o) =>
        defaultValues.find((dV) => dV === o.value)
      );
      onChange(values);
    }
    if (
      JSON.stringify(lastSelectedDepartment) !==
      JSON.stringify(selectedDepartment)
    ) {
      setLastSelectedDepartment(selectedDepartment);
    }

    if (
      users &&
      Array.isArray(users) &&
      users.length > 0 &&
      !loading &&
      projects &&
      defaultOptions.length === 0 &&
      Array.isArray(projects) &&
      projects.length > 0 &&
      (name === "resource1" || name === "resource2") &&
      JSON.stringify(lastProjects) !== JSON.stringify(projects)
    ) {
      setLastProjects(projects);
      actions.toggleLoader(true);
      let data = [];
      if (name === "resource1") {
        data = users.filter((user) =>
          projects.find(
            (project) =>
              project.resource1 &&
              project.resource1.toString() === user._id.toString()
          )
        );
      } else if (name === "resource2") {
        data = users.filter((user) =>
          projects.find(
            (project) =>
              project.resource2 &&
              project.resource2.toString() === user._id.toString()
          )
        );
      }
      setDefaultOptions(
        data.map(({ _id, full_name, department }) => ({
          value: _id,
          label: full_name,
          department
        }))
      );
      actions.toggleLoader(false);
    }

    if (
      users &&
      Array.isArray(users) &&
      users.length > 0 &&
      defaultOptions.length === 0 &&
      !loading &&
      !projects &&
      !(name === "resource1" || name === "resource2")
    ) {
      setDefaultOptions(
        users.map(({ _id, full_name, department }) => ({
          value: _id,
          label: full_name,
          department
        }))
      );
    }
  }, [
    users,
    defaultOptions,
    loading,
    lastSelectedDepartment,
    selectedDepartment,
    defaultValues,
    disabled,
    initialValue,
    isMulti,
    onChange,
    actions,
    projects,
    name,
    lastProjects
  ]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise((resolve) => {
        try {
          actions.toggleLoader(true);
          let defaultUsers = [];
          if (
            users &&
            users.length > 0 &&
            !loading &&
            inputValue.length > 0 &&
            defaultOptions.length > 0
          ) {
            if (
              lastSelectedDepartment &&
              Array.isArray(lastSelectedDepartment) &&
              lastSelectedDepartment.length > 0 &&
              JSON.stringify(lastSelectedDepartment) ===
                JSON.stringify(selectedDepartment)
            ) {
              defaultUsers = defaultOptions.filter(
                (user) =>
                  lastSelectedDepartment.some(
                    (department) => department.value === user.department._id
                  ) &&
                  user.label.toLowerCase().includes(inputValue.toLowerCase())
              );
            } else {
              defaultUsers = defaultOptions.filter((user) =>
                user.label.toLowerCase().includes(inputValue.toLowerCase())
              );
            }
          }
          resolve(defaultUsers);
        } catch (err) {
          toast.error();
        } finally {
          actions.toggleLoader(false);
        }
      }),
    [
      actions,
      users,
      loading,
      defaultOptions,
      lastSelectedDepartment,
      selectedDepartment
    ]
  );

  const getValue = () => {
    if (isMulti && Array.isArray(initialValue)) {
      return (
        defaultOptions.filter((x) =>
          initialValue.find((i) => i.value === x.value || i === x.value)
        ) || []
      );
    }
    if (initialValue) {
      return defaultOptions?.filter((x) => updatedValue === x?.value)?.[0];
    }
    return null;
  };

  const handleChange = (v) => {
    if (isMulti && !Array.isArray(v)) {
      onChange([]);
    } else {
      onChange(v);
    }
  };

  return (
    <React.Fragment>
      {label ? (
        <label
          className={`control-label ${!label ? "hidden" : ""}`}
          htmlFor={name}
          style={{ textTransform: "uppercase" }}
        >
          {label}
        </label>
      ) : null}
      <AsyncSelect
        name={name}
        loadOptions={loadingOptions}
        isMulti={isMulti}
        className="react-select-container"
        classNamePrefix="react-select-container"
        placeholder={placeholder}
        value={getValue()}
        isLoading={loading || disabled}
        defaultOptions={defaultOptions}
        isClearable
        cacheOptions
        onChange={handleChange}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length ? "No options" : "Start typing..."
        }
        isDisabled={disabled}
      />
    </React.Fragment>
  );
};

UserSelect.propTypes = {
  value: PropTypes.any,
  name: PropTypes.string,
  onChange: PropTypes.func,
  users: PropTypes.array,
  actions: PropTypes.object,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
  selectedDepartment: PropTypes.array,
  disabled: PropTypes.bool,
  defaultValues: PropTypes.array,
  loading: PropTypes.bool,
  projects: PropTypes.array,
  label: PropTypes.string
};

UserSelect.defaultProps = {
  selectedDepartment: null,
  projects: null,
  disabled: false,
  defaultValues: null,
  users: []
};

function mapStatesToProps(state) {
  return {
    users: state.reducers.users,
    loading: state.reducers.loading
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...userActions,
        ...loaderActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(UserSelect);
